import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { SocietyEquityMarketStatus } from 'types/Society';
import isSuperAdmin from 'utils/isSuperAdmin';
import { isPlatformAXA } from 'utils/showDetails';

type BeneficiaryLike = {
  _id?: string;
  sharesCount?: { actual?: number };
  configuration?: {
    incentivesMarket?: { enabled?: boolean };
  };
};

type SocietyLike = {
  _id?: string;
  societyId?: string;
  configuration?: {
    incentivesMarket?: {
      enabled?: boolean;
      status?: SocietyEquityMarketStatus;
    };
  };
};

export const useBeneficiaryIncentivesMarket = ({
  beneficiary,
  society,
}: {
  beneficiary?: BeneficiaryLike | null;
  society?: SocietyLike | null;
}) => {
  const history = useHistory();
  const { societyId: societyIdFromRoute } = useParams<{ societyId?: string }>();
  const user = useSelector((state: { user?: { _id?: string } }) => state.user);
  const actualSocietyId = useSelector(
    (state: State) => state.society?.actualSociety?._id
  );
  const isAdmin = useSelector(
    (state: { society?: { role?: { isAdmin?: boolean } } }) =>
      state.society?.role?.isAdmin ?? false
  );

  const [hasIncentivesMarketEnabled, setHasIncentivesMarketEnabled] =
    useState(false);

  useEffect(() => {
    if (!user) return;

    const isSocietyIncentivesMarketEnabled =
      !!society?.configuration?.incentivesMarket?.enabled;

    const isMarketUnavailable =
      society?.configuration?.incentivesMarket?.status ===
      SocietyEquityMarketStatus.UNAVAILABLE;

    const isBeneficiaryAllowed = (beneficiary?.sharesCount?.actual || 0) > 0;

    const isBeneficiaryIncentivesMarketAllowed =
      !!beneficiary?.configuration?.incentivesMarket?.enabled;

    const isUserSuperAdmin = isSuperAdmin(user);

    if (isMarketUnavailable || !isSocietyIncentivesMarketEnabled) {
      setHasIncentivesMarketEnabled(false);
      return;
    }

    const isVisibleForAXA = Boolean(
      isPlatformAXA &&
        isSocietyIncentivesMarketEnabled &&
        isBeneficiaryIncentivesMarketAllowed &&
        isBeneficiaryAllowed &&
        isAdmin
    );
    const isVisibleForSttok = Boolean(
      (!isPlatformAXA &&
        isSocietyIncentivesMarketEnabled &&
        isBeneficiaryIncentivesMarketAllowed &&
        isBeneficiaryAllowed) ||
        isUserSuperAdmin
    );

    setHasIncentivesMarketEnabled(isVisibleForAXA || isVisibleForSttok);
  }, [user, beneficiary, society, isAdmin]);

  const handleOpenIncentivesMarket = () => {
    const resolvedSocietyId =
      society?.societyId || society?._id || societyIdFromRoute || actualSocietyId;

    if (!resolvedSocietyId) {
      return;
    }

    const queryParam = beneficiary?._id ? `?beneficiaryId=${beneficiary._id}` : '';
    history.push(`/mercado-de-incentivos/${resolvedSocietyId}${queryParam}`);
  };

  return {
    hasIncentivesMarketEnabled,
    handleOpenIncentivesMarket,
  };
};
